import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import {   
  saveAllTables,
  calculateFinancialSummary,
  setBool, 
  getVal
} from '../helpers/process_morningstar.js';

import {useAtomValue} from 'jotai';
import { emailAtom } from './Atoms.js';

import { fetchGraphData } from '../helpers/graph_middleware.js';

import AnnualFinancialSummary from './CompanyDetailComponents/AnnualFinancialSummary.js';
import FinancialSummary from './CompanyDetailComponents/FinancialSummary.js';
import AnnualDividends from './CompanyDetailComponents/AnnualDividends.js';
import Dividends from './CompanyDetailComponents/Dividends.js';
import AnnualCFandIncome from './CompanyDetailComponents/AnnualCFandIncome.js'; 
import CFandIncome from './CompanyDetailComponents/CFandIncome.js';
import AnnualInvestedCapital from './CompanyDetailComponents/AnnualInvestedCapital.js';
import InvestedCapital from './CompanyDetailComponents/InvestedCapital.js';
import AnnualAssetLife from './CompanyDetailComponents/AnnualAssetLife.js';
import AssetLife from './CompanyDetailComponents/AssetLife.js';
import AnnualSalvageValue from './CompanyDetailComponents/AnnualSalvageValue.js';
import SalvageValue from './CompanyDetailComponents/SalvageValue.js';
import AnnualReturnOnCapital from './CompanyDetailComponents/AnnualReturnOnCapital.js';
import ReturnOnCapital from './CompanyDetailComponents/ReturnOnCapital.js';
import Graphs from './CompanyDetailComponents/Graphs.js';
import MorningStar from './CompanyDetailComponents/MorningStar.js';
import ProgVargs from './CompanyDetailComponents/ProgVars.js'

import TableTabs from './CompanyDetailComponents/TableTabs.js';
import TickerSearch from './TickerSearch.js';
import Comparables from './CompanyDetailComponents/Comparables.js';
import Header from './CompanyDetailComponents/Header.js'

import PropTypes from 'prop-types';
import {
  Divider,
  Button,
  Container,
  CircularProgress,
  Drawer,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  IconButton,
  Select,
  Menu,
  MenuItem,
  Grid,
  Box,
  Tabs,
  Tab
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import { pdf } from "@react-pdf/renderer";
import { Pdf, DownloadInvoiceButton } from './Pdf.js';

const WEBAPI = process.env.REACT_APP_WEB_API;

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function CompanyDetail() {
  const { ticker } = useParams();
  const upperTicker = ticker?.toUpperCase();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTable, setSelectedTable] = useState("Summary");
  const [msData, setMsData] = useState([]);
  const [capRD, setCapRD] = useState(null);
  const [intangibles, setIntangibles] = useState(null)
  const [includeOtherIncome, setIncludeOtherIncome] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  const [nav, setNav] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [chartImage, setChartImage] = useState([]);
  const [description, setDescription] = useState("");
  const [readyToDownload, setReadyToDownload] = useState(false);
  const [tablePeriod, setTablePeriod] = useState("Annual"); // new state for dropdown
  const [openMenu, setOpenMenu] = useState(false)
  const [graphInfo, setGraphInfo] = useState([])
  
  const chartRef = useRef(null);

  const userEmail = useAtomValue(emailAtom);
  
  const adminEmails = ["mkacur@fsavaluation.com", "ken@afxsoftware.com", "kenc@team-eagle.ca"];
  const isAdmin = adminEmails.includes(userEmail);

  const setSelectedTableAndCloseMenu = (table) => {
    setSelectedTable(table);
    setOpenMenu(false);
    console.log("setSelectedTableAndCloseMenu:", table);
  };

const tableComponents = [
  { tableName: "Summary", component: <Graphs onImagesReady={setChartImage} descriptionData={setDescription} /> },
  { tableName: "Financials", component: tablePeriod === "Quarterly" ? <FinancialSummary /> : <AnnualFinancialSummary /> },
  { tableName: "Dividends", component: tablePeriod === "Quarterly" ? <Dividends /> : <AnnualDividends /> },
  { tableName: "CFIRR And Returns", component: tablePeriod === "Quarterly" ? <ReturnOnCapital /> : <AnnualReturnOnCapital /> },
  { tableName: "Cash Flow & Income", component: tablePeriod === "Quarterly" ? <CFandIncome /> : <AnnualCFandIncome /> },
  { tableName: "Invested Capital", component: tablePeriod === "Quarterly" ? <InvestedCapital /> : <AnnualInvestedCapital /> },
  { tableName: "Asset Life", component: tablePeriod === "Quarterly" ? <AssetLife /> : <AnnualAssetLife /> },
  { tableName: "Salvage Value", component: tablePeriod === "Quarterly" ? <SalvageValue /> : <AnnualSalvageValue /> },
  ...(isAdmin
    ? [{ tableName: "MorningStar", component: <MorningStar /> },
      { tableName: "ProgVars", component: <ProgVargs /> }]
    : []),
  ];

  const handleTicker = (newTicker) => {
    if (!newTicker) return;
    navigate(`/${newTicker.toLowerCase()}`); // 👈 This triggers re-render with new ticker
  };

  // Fetch tables from backend and save in IndexedDB
  useEffect(() => {
    const fetchTables = async () => {
      try {

        // Reset UI before loading new data
        setTables([]);
        setError(null);
        setCompanyName("");

        // Delete old database first
        indexedDB.deleteDatabase(`CompanyTables`);

        if (upperTicker === undefined) {
            setError('Ticker is undefined');
            return;
        }

        const res = await axios.get(`${WEBAPI}/ticker/msdata/${upperTicker}`);

        setMsData(res.data);
        setCompanyName(res.data[0].tableData[3][1]);

        const tablesWithObjects = res.data.map(t => ({
          tableName: t.tableName,
          tableData: Array.isArray(t.tableData) ? t.tableData : []
        }));

        // Save backend tables in IndexedDB
        await saveAllTables(tablesWithObjects);

        setBool("capRD", true); // Default to true  
        setBool("includeOtherIncome", false); // Default to false
        setBool("selectedTax", false); // Default to false
        setBool('intangibles', false);
        localStorage.setItem('dateRange', '5Y')
        localStorage.setItem('period', 'Annual')


        setCapRD(getVal("capRD"));
        setIncludeOtherIncome(getVal("includeOtherIncome"));
        setSelectedTax(getVal("selectedTax"));
        setIntangibles(getVal('intangibles'))

        // Calculate Financial Summary using helper
        const financialSummaryData = await calculateFinancialSummary(capRD, includeOtherIncome, selectedTax);
        const graphData = await fetchGraphData(capRD, includeOtherIncome, selectedTax);

        setGraphInfo(graphData)



        setTables([
          { tableName: "Financial Summary", tableData: financialSummaryData },
          { tableName: "Dividends", tableData: financialSummaryData },
          { tableName: "CF and Income", tableData: financialSummaryData },
          { tableName: "Invested Capital", tableData: financialSummaryData },
          { tableName: "Asset Life", tableData: financialSummaryData },
          { tableName: "Salvage Value", tableData: financialSummaryData },
          { tableName: "Graphs", tableData: graphData },
          { tableName: "MorningStar" },
          { tableName: "ProgVars" }
        ]);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchTables();
  }, [upperTicker]);

  const handleChange = (event, newValue) => {
    setNav(newValue);
    setOpenMenu(false)
  };
  
  if (error) return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "90vh", // full screen height
      }}
      >
        Error: Not A Valid Ticker
      </div>
    );

  if (!tables.length) return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "90vh", // full screen height
      }}
    >
      <h2>Loading {ticker.toUpperCase()}...</h2>
      <CircularProgress />
    </div>
  );

    const selectedComponent = tableComponents.find(
      (t) => t.tableName === selectedTable
    )?.component;

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid
          item
          sx={{
            display: { xs: "none", lg: "block" },
            width: 220,
            flexShrink: 0,
          }}
        >
          {/* <Paper sx={{ p: 1 }}> */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                alignItems: "flex-start",
                p: 0,
              }}
            >
            <Box
              sx={{
                width: "100%",
                mb: 2,
                "& .MuiAutocomplete-root": { width: "100% !important" },
                "& .MuiInputBase-root": { width: "100% !important", fontSize: "0.75rem" },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                "& .MuiAutocomplete-option": { fontSize: "0.75rem" },
                "& .MuiAutocomplete-popupIndicator": { display: "none" },
              }}
            >
              <TickerSearch handleAddTicker={handleTicker} fromCompanyDetail={true} />
            </Box>

            <TableTabs
              selectedTable={selectedTable}
              setSelectedTable={setSelectedTable}
              setTablePeriod={setTablePeriod}
              tableComponents={tableComponents}
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: "-8px" }}>
            Powered By:  
            <img
              src="/fsa_logo.png"
              alt="fsa logo"
              style={{
                width: "auto",
                height: "38px",
                marginLeft: "2px",
                verticalAlign: "middle"
              }}
            />
          </Typography>

          <Link
            to="https://fsavaluation.com/"
            target="_blank" 
            rel="noopener noreferrer"
          >
            www.fsavaluation.com
          </Link>

          <Typography variant="subtitle2">
            416 453-5875
          </Typography>

        </Grid>

        <Drawer
          anchor="left"
          open={openMenu}
          onClose={() => setOpenMenu(false)}
          // sx={{ display: { xs: "block", md: "none" } }}
        >
          <Box sx={{ width: 200, p: 2 }}>
            <Box
              sx={{
                width: "100%",
                mb: 2,
                "& .MuiAutocomplete-root": { width: "100% !important" },
                "& .MuiInputBase-root": { width: "100% !important", fontSize: "0.75rem" },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                "& .MuiAutocomplete-option": { fontSize: "0.75rem" },
                "& .MuiAutocomplete-popupIndicator": { display: "none" },
              }}
            >
              <TickerSearch handleAddTicker={handleTicker} fromCompanyDetail={true} />
            </Box>

            <TableTabs
              selectedTable={selectedTable}
              setSelectedTable={setSelectedTableAndCloseMenu}
              setTablePeriod={setTablePeriod}
              tableComponents={tableComponents}
            />
          </Box>
        </Drawer>




        <Grid
          item
          xs={12}
          sm
          md
          sx={{
            minWidth: 0,
          }}
        >

          <Paper sx={{ p: 1 }}>
          <Box >
            <IconButton
              sx={{ display: { md: "block", lg: "none" } }}
              onClick={() => setOpenMenu(true)}
            >
              <MenuIcon />
            </IconButton>
          </Box>

              <Header 
                graphData={graphInfo}
                pdfImage={chartImage} 
                pdfDescription={description}
              />

              {selectedComponent}
          </Paper>
        </Grid>

        {selectedTable === "Summary" ? (
          <Grid
            item
            xs={12}
            sm="auto"
            sx={{
              width: { sm: "100%", md: 310 },
              flexShrink: 0,
            }}
          >
            <Paper sx={{ p: 1 }}>
              <Comparables />
            </Paper>
          </Grid>
        ) : null}        

      </Grid>



    </Box>
  );
}